import axios from "axios";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
  Text,
  Heading,
  VStack,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react";
import { trackClick } from '../ga';
export default function ContactForm() {
  const orgColor = "#ec562c";
  const bluColor = "#2f4280";
  const toast = useToast();
  const [captchaToken, setCaptchaToken] = useState("");
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
 

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

const onSubmit = async (data) => {
  if (!captchaToken) {
    toast({
      title: isArabic ? "الرجاء التحقق من reCAPTCHA" : "reCAPTCHA required",
      description: isArabic ? "يرجى التحقق من أنك لست روبوتًا." : "Please verify that you are not a robot.",
      status: "warning",
      duration: 5000,
      isClosable: true,
    });
    return;
  }

  try {
    await axios.post("https://itracks-api-prod-22dc11310f73.herokuapp.com/api/contact", {
      ...data,
      captchaToken
    });

    toast({
      title: isArabic ? "تم الإرسال بنجاح" : "Submitted Successfully",
      description: isArabic ? "سنتواصل معك قريبًا." : "We'll get back to you soon.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });

    reset();
    setCaptchaToken("");
  } catch (error) {
    toast({
      title: isArabic ? "فشل الإرسال" : "Submission Failed",
      description: isArabic
        ? "حدث خطأ أثناء الإرسال، حاول مرة أخرى لاحقًا."
        : "Please try again later.",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  }
};


  return (
    <Box bgGradient="linear(to-b, #ffffff, #2f4280)" py={16} px={6}   onClick={() => trackClick(`Clicked contact page`)}>
      <Heading
        textAlign="center"
        mb={10}
        fontSize="4xl"
        color={bluColor}
        fontWeight="bold"
      >
        {i18n.language === "ar" ? " تواصل معنا " : "       Get in Touch"}
      </Heading>

      <Flex
        direction={{ base: "column", md: "row" }}
        gap={10}
        maxW="7xl"
        mx="auto"
        align="stretch"
      >
        {/* FORM */}
        <Box
          flex="1"
          bg="white"
          p={10}
          borderRadius="2xl"
          boxShadow="2xl"
          borderLeft={`6px solid ${orgColor}`}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={5}>
              <FormControl isInvalid={errors.name}>
                <FormLabel color="gray.700">

                  {i18n.language === "ar" ? " الاسم" : "Name"}
                </FormLabel>
                <Input
                  {...register("name", { required: "Name is required" })}
                  placeholder={i18n.language === "ar" ? "اسمك" : "Your Name "}
                />
                {errors.name && (
                  <Text color="red.500" fontSize="sm">
                    {errors.name.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.phone}>
                <FormLabel color="gray.700">
                  {i18n.language === "ar" ? "رقم الجوال" : "Phone Number"}
                </FormLabel>
                <Input
                  {...register("phone", {
                    required: "Phone number is required",
                  })}
                  placeholder={i18n.language === "ar" ? "رقم الجوال" : "Your Phone"}
                />
                {errors.phone && (
                  <Text color="red.500" fontSize="sm">
                    {errors.phone.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.email}>
                <FormLabel color="gray.700">
                  {i18n.language === "ar" ? "البريد الإلكتروني" : "Email"}
                </FormLabel>
                <Input
                  {...register("email", {
                    required: "Email is required",
                  })}
                  type="email"
                  placeholder="name@example.com"
                />
                {errors.email && (
                  <Text color="red.500" fontSize="sm">
                    {errors.email.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.message}>
                <FormLabel color="gray.700">
                  {i18n.language === "ar" ? "الرسالة" : "Message"}
                </FormLabel>
                <Textarea
                  {...register("message", {
                    required: "Message is required",
                  })}
                  placeholder={i18n.language === "ar" ? "اكتب رسالتك" : "Write your message..."}
                />
                {errors.message && (
                  <Text color="red.500" fontSize="sm">
                    {errors.message.message}
                  </Text>
                )}
              </FormControl>

              <Box mt={2}>
                <ReCAPTCHA
                  sitekey="6LclbXwrAAAAAD37HOEtRBRV9rz9qGME9Py17Xus"
                  onChange={(token) => setCaptchaToken(token)}
                />
              </Box>

              <Button
                type="submit"
                colorScheme="orange"
                bg={orgColor}
                color="white"
                _hover={{ bg: "#cf4722" }}
                isLoading={isSubmitting}
                width="full"
                isDisabled={!captchaToken}
              >
                {i18n.language === "ar" ? "   ارسال" : "       Submit "}
              </Button>
            </VStack>
          </form>
        </Box>

        {/* MAP */}
        <Box
          flex="1"
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="2xl"
          minH={"auto"}
        >
          <iframe
            title="Company Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3618.1039335242786!2d46.635911!3d24.795436!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e2ee33eaae1d343%3A0x1671c149938e6b41!2zVGhlIERlc3RpbmF0aW9uIC0g2LDYpyDYr9iz2KrZhtmK2LTZhg!5e0!3m2!1sen!2ssa!4v1751134591873!5m2!1sen!2ssa"
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: "100%" }}
            allowFullScreen=""
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Box>
      </Flex>
    </Box>
  );
}